import React from "react";
import { PropTypes } from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
// Modern imports
import ExpandMoreIcon from "@limitless/icons/ExpandMoreIcon";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Grid from "@limitless/modern/layout/Grid";
import ModernTaskFiltersCheckbox from "./ModernTaskFiltersCheckbox";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import Lock from "@limitless/icons/Lock";

const useStyles = makeStyles(theme => ({
    customAccordion: {
        margin: "0px!important",
        display: "flex",
        alignItems: "center"
    },
    customAccordionRoot: {
        padding: 0,
        border: "0px",
        "&:before": {
            border: "0px",
            backgroundColor: "transparent"
        }
    },
    customAccordionSummaryRoot: {
        minHeight: "32px",
        padding: 0
    }
}));

const ModernTaskListTenantSelection = ({ filters, onGroupChange, groupByTenantName, isSecureAccess }) => {
    const classes = useStyles();

    const height = window.innerHeight;
    const [expanded, setExpanded] = React.useState();

    const handleTenantOrGroupChange = (checked, onGroupChange, appendValue, filterValue) => {
        const { selectedGroups } = filters;
        if (checked) {
            onGroupChange([...selectedGroups.value, ...appendValue], appendValue, []);
        } else {
            const isTenantName = selectedGroups.value.some(item => item.tenantDisplayName === filterValue);
            const checkedGroups = selectedGroups.value.filter(item =>
                isTenantName ? item.tenantDisplayName !== filterValue : item.value !== filterValue
            );
            const unCheckedGroups = selectedGroups.value.filter(item =>
                isTenantName ? item.tenantDisplayName === filterValue : item.value === filterValue
            );
            onGroupChange(checkedGroups, checkedGroups, unCheckedGroups);
        }
    };

    return (
        <Grid
            sx={{
                marginTop: 2
            }}
        >
            <Grid
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                <Typography variant="body2">{"Brand"}</Typography>
                {isSecureAccess && (
                    <IconButton
                        icon={<Lock />}
                        label="Displaying tasks available from your Secure Location only"
                        id="secureAccessLockIcon"
                        sx={{
                            width: 23,
                            height: 23
                        }}
                    />
                )}
            </Grid>
            <Grid sx={{ height: "auto", overflowY: "auto", overflowX: "hidden" }}>
                {Object.keys(groupByTenantName)
                    .sort()
                    .map((key, index) => {
                        const tenantChecked = filters.selectedGroups.value.filter(selectedGroup => {
                            return selectedGroup.tenantDisplayName === key;
                        });

                        return (
                            <Accordion
                                disableGutters
                                classes={{ root: classes.customAccordionRoot }}
                                key={index}
                                expanded={expanded === key}
                                onChange={() => (key === expanded ? setExpanded("") : setExpanded(key))}
                            >
                                <AccordionSummary
                                    classes={{
                                        content: classes.customAccordion,
                                        root: classes.customAccordionSummaryRoot
                                    }}
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <ModernTaskFiltersCheckbox
                                        label={key}
                                        onChange={(e, checked) => {
                                            handleTenantOrGroupChange(
                                                checked,
                                                onGroupChange,
                                                groupByTenantName[key],
                                                key
                                            );
                                        }}
                                        checked={tenantChecked[0]?.tenantDisplayName === key}
                                        labelVariant="body2"
                                        includeAvatar={false}
                                        tenantName={key}
                                    />
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        paddingBottom: 0,
                                        paddingTop: 0,
                                        paddingLeft: "6px"
                                    }}
                                >
                                    {groupByTenantName[key]
                                        .sort((group1, group2) => {
                                            let groupA = group1.displayName?.toUpperCase();
                                            let groupB = group2.displayName?.toUpperCase();
                                            return groupA < groupB ? -1 : groupA > groupB ? 1 : 0;
                                        })
                                        .map((group, groupIndex) => {
                                            const checked =
                                                filters.selectedGroups.value.filter(
                                                    selectedGroup => selectedGroup?.value === group.value
                                                ).length > 0;

                                            return (
                                                <Grid container key={groupIndex} alignItems="center">
                                                    <ModernTaskFiltersCheckbox
                                                        label={group.displayName}
                                                        onChange={(e, checked) => {
                                                            handleTenantOrGroupChange(
                                                                checked,
                                                                onGroupChange,
                                                                [group],
                                                                group.value
                                                            );
                                                        }}
                                                        sx={{ padding: "5px", paddingLeft: "3px" }}
                                                        checked={checked}
                                                        labelVariant="body2"
                                                        includeAvatar={false}
                                                    />
                                                </Grid>
                                            );
                                        })}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
            </Grid>
        </Grid>
    );
};

export default ModernTaskListTenantSelection;

ModernTaskListTenantSelection.propTypes = {
    filters: PropTypes.object.isRequired,
    groupByTenantName: PropTypes.object.isRequired,
    onGroupChange: PropTypes.func.isRequired,
    isSecureAccess: PropTypes.bool.isRequired
};
